// Error Handling Service
// Provides centralized error handling and user-friendly error messages

import { AxiosError } from 'axios';

export interface ApiError {
  message: string;
  code?: string;
  statusCode?: number;
  details?: any;
  timestamp: string;
  retryable: boolean;
}

export interface ErrorContext {
  operation: string;
  component?: string;
  userId?: string;
  additionalData?: any;
}

export class ErrorHandlerService {
  private static instance: ErrorHandlerService;

  private constructor() {}

  public static getInstance(): ErrorHandlerService {
    if (!ErrorHandlerService.instance) {
      ErrorHandlerService.instance = new ErrorHandlerService();
    }
    return ErrorHandlerService.instance;
  }

  /**
   * Convert various error types to standardized ApiError
   */
  public handleError(error: any, context?: ErrorContext): ApiError {
    const timestamp = new Date().toISOString();
    
    // Handle Axios errors (HTTP errors)
    if (this.isAxiosError(error)) {
      return this.handleAxiosError(error, context, timestamp);
    }

    // Handle network errors
    if (this.isNetworkError(error)) {
      return this.handleNetworkError(error, context, timestamp);
    }

    // Handle timeout errors
    if (this.isTimeoutError(error)) {
      return this.handleTimeoutError(error, context, timestamp);
    }

    // Handle generic JavaScript errors
    if (error instanceof Error) {
      return this.handleGenericError(error, context, timestamp);
    }

    // Handle unknown errors
    return this.handleUnknownError(error, context, timestamp);
  }

  private isAxiosError(error: any): error is AxiosError {
    return error.isAxiosError === true;
  }

  private isNetworkError(error: any): boolean {
    return error.code === 'NETWORK_ERROR' || 
           error.message?.includes('Network Error') ||
           error.code === 'ERR_NETWORK';
  }

  private isTimeoutError(error: any): boolean {
    return error.code === 'ECONNABORTED' || 
           error.message?.includes('timeout') ||
           error.code === 'TIMEOUT';
  }

  private handleAxiosError(error: AxiosError, context?: ErrorContext, timestamp?: string): ApiError {
    const response = error.response;
    const statusCode = response?.status;
    const responseData = response?.data as any;

    // Extract error message from response
    let message = 'An unexpected error occurred';
    if (responseData?.error) {
      message = responseData.error;
    } else if (responseData?.message) {
      message = responseData.message;
    } else if (error.message) {
      message = error.message;
    }

    // Determine if error is retryable
    const retryable = this.isRetryableStatusCode(statusCode);

    // Create user-friendly messages based on status code
    switch (statusCode) {
      case 400:
        message = responseData?.error || 'Invalid request. Please check your input and try again.';
        break;
      case 401:
        message = 'Authentication required. Please log in and try again.';
        break;
      case 403:
        message = 'You do not have permission to perform this action.';
        break;
      case 404:
        message = responseData?.error || 'The requested resource was not found.';
        break;
      case 409:
        message = responseData?.error || 'A conflict occurred. The resource may already exist.';
        break;
      case 422:
        message = responseData?.error || 'The request could not be processed. Please check your input.';
        break;
      case 429:
        message = 'Too many requests. Please wait a moment and try again.';
        break;
      case 500:
        message = 'A server error occurred. Please try again later.';
        break;
      case 502:
      case 503:
      case 504:
        message = 'The service is temporarily unavailable. Please try again later.';
        break;
    }

    this.logError(error, context, statusCode);

    return {
      message,
      code: error.code,
      statusCode,
      details: responseData,
      timestamp: timestamp || new Date().toISOString(),
      retryable
    };
  }

  private handleNetworkError(error: any, context?: ErrorContext, timestamp?: string): ApiError {
    this.logError(error, context);

    return {
      message: 'Network connection failed. Please check your internet connection and try again.',
      code: 'NETWORK_ERROR',
      statusCode: 0,
      details: error,
      timestamp: timestamp || new Date().toISOString(),
      retryable: true
    };
  }

  private handleTimeoutError(error: any, context?: ErrorContext, timestamp?: string): ApiError {
    this.logError(error, context);

    return {
      message: 'The request timed out. The server may be busy. Please try again.',
      code: 'TIMEOUT',
      statusCode: 408,
      details: error,
      timestamp: timestamp || new Date().toISOString(),
      retryable: true
    };
  }

  private handleGenericError(error: Error, context?: ErrorContext, timestamp?: string): ApiError {
    this.logError(error, context);

    return {
      message: error.message || 'An unexpected error occurred',
      code: 'GENERIC_ERROR',
      details: error,
      timestamp: timestamp || new Date().toISOString(),
      retryable: false
    };
  }

  private handleUnknownError(error: any, context?: ErrorContext, timestamp?: string): ApiError {
    this.logError(error, context);

    return {
      message: 'An unknown error occurred. Please try again.',
      code: 'UNKNOWN_ERROR',
      details: error,
      timestamp: timestamp || new Date().toISOString(),
      retryable: false
    };
  }

  private isRetryableStatusCode(statusCode?: number): boolean {
    if (!statusCode) return false;
    
    // Retryable status codes
    const retryableCodes = [408, 429, 500, 502, 503, 504];
    return retryableCodes.includes(statusCode);
  }

  private logError(error: any, context?: ErrorContext, statusCode?: number): void {
    const logData = {
      error: {
        message: error.message,
        code: error.code,
        statusCode,
        stack: error.stack
      },
      context,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href
    };

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('API Error:', logData);
    }

    // In production, you might want to send errors to a logging service
    // Example: sendToLoggingService(logData);
  }

  /**
   * Get user-friendly error message for display
   */
  public getUserMessage(error: ApiError, operation?: string): string {
    let message = error.message;

    // Add context if operation is provided
    if (operation) {
      const operationMessages: Record<string, string> = {
        'create_node': 'Failed to create warehouse node',
        'update_node': 'Failed to update warehouse node',
        'delete_node': 'Failed to delete warehouse node',
        'fetch_nodes': 'Failed to load warehouse nodes',
        'update_inventory': 'Failed to update inventory',
        'fetch_inventory': 'Failed to load inventory data',
        'update_demand': 'Failed to update demand',
        'fetch_demand': 'Failed to load demand data',
        'run_optimization': 'Failed to run optimization',
        'fetch_gaps': 'Failed to calculate demand gaps'
      };

      const operationMessage = operationMessages[operation];
      if (operationMessage) {
        message = `${operationMessage}: ${message}`;
      }
    }

    return message;
  }

  /**
   * Determine if an error should show a retry button
   */
  public shouldShowRetry(error: ApiError): boolean {
    return error.retryable;
  }

  /**
   * Get suggested actions for the user based on error type
   */
  public getSuggestedActions(error: ApiError): string[] {
    const actions: string[] = [];

    if (error.retryable) {
      actions.push('Try again');
    }

    switch (error.statusCode) {
      case 0: // Network error
        actions.push('Check your internet connection');
        actions.push('Refresh the page');
        break;
      case 400:
        actions.push('Check your input and try again');
        break;
      case 401:
        actions.push('Log in and try again');
        break;
      case 403:
        actions.push('Contact your administrator');
        break;
      case 404:
        actions.push('Refresh the page');
        actions.push('Check if the resource still exists');
        break;
      case 409:
        actions.push('Use a different name');
        actions.push('Check existing resources');
        break;
      case 408:
      case 429:
      case 500:
      case 502:
      case 503:
      case 504:
        actions.push('Wait a moment and try again');
        actions.push('Contact support if the problem persists');
        break;
    }

    return actions;
  }
}

// Export singleton instance
export const errorHandler = ErrorHandlerService.getInstance();

// Helper functions
export const handleApiError = (error: any, context?: ErrorContext) => 
  errorHandler.handleError(error, context);

export const getUserErrorMessage = (error: ApiError, operation?: string) => 
  errorHandler.getUserMessage(error, operation);

export const shouldShowRetryButton = (error: ApiError) => 
  errorHandler.shouldShowRetry(error);

export const getErrorActions = (error: ApiError) => 
  errorHandler.getSuggestedActions(error);